import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsShell } from "./SettingsShell";
import { useAuth } from "@/context/AuthContext";

export default function SoftwareBrandingSettings() {
    const { user } = useAuth();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/branding/software").then(({ data }) => setForm(data));
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/branding/software", {
                product_name: form.product_name,
                tagline: form.tagline,
                logo_url: form.logo_url,
                primary_color: form.primary_color,
                contact_email: form.contact_email,
                support_email: form.support_email,
                docs_url: form.docs_url,
            });
            toast.success("Software branding updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const extraTabs = user?.is_super_admin ? [{ to: "/app/settings/software-branding", label: "Software Branding" }] : [];

    return (
        <SettingsShell title="Software branding" subtitle="Platform-wide product branding — visible on the software marketing site. Super admin only." extraTabs={extraTabs}>
            <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="software-branding-form">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label>Product name</Label>
                        <Input value={form.product_name || ""} onChange={(e) => set("product_name", e.target.value)} className="mt-2" data-testid="sb-product-name" />
                    </div>
                    <div>
                        <Label>Primary color</Label>
                        <div className="mt-2 flex items-center gap-2">
                            <Input type="color" value={form.primary_color || "#0F766E"} onChange={(e) => set("primary_color", e.target.value)} className="w-16 h-10 p-1" />
                            <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="flex-1" data-testid="sb-primary-color" />
                        </div>
                    </div>
                </div>
                <div>
                    <Label>Tagline</Label>
                    <Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="mt-2" data-testid="sb-tagline" />
                </div>
                <div>
                    <Label>Logo URL</Label>
                    <Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className="mt-2" data-testid="sb-logo-url" />
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <Label>Contact email</Label>
                        <Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                        <Label>Support email</Label>
                        <Input value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} className="mt-2" />
                    </div>
                    <div>
                        <Label>Docs URL</Label>
                        <Input value={form.docs_url || ""} onChange={(e) => set("docs_url", e.target.value)} className="mt-2" />
                    </div>
                </div>
                <div className="border-t border-border pt-6">
                    <button type="submit" disabled={saving} className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50" data-testid="sb-save">
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </form>
        </SettingsShell>
    );
}
